/* One rung of the picker: a caption over a row of buttons. The tree, the creator and the
   range-source choice are all built out of it. Options the collection has no chart for are
   greyed out, so gaps in the collection are visible at a glance; options the rules forbid are
   dead, and say why. */

import { el } from "./dom";

export interface GroupOptions {
  /** Which rung this is, as a stable hook: `data-dim` on the group. */
  dim?: string;
  /** What a button shows, when the stored value is too long to be one. */
  labelOf?: (value: string) => string;
  /** A colour to set a dot in before a button's label, or "" for no dot. The opponent rung
      uses it: a profile is told apart by its colour at the table, so it wears it here too. */
  dotOf?: (value: string) => string;
  /** A stable hook on one button, as `data-value`. */
  keyOf?: (value: string) => string;
  /** What a button stands for, on hover. */
  titleOf?: (value: string) => string;
  /** Why a value can never be picked here, or "" when it can. */
  faultOf?: (value: string) => string;
  /** Whether the page is in edit mode: what turns a gap into an invitation. */
  editing?: boolean;
  /** Edit mode only: a value with no chart behind it is clicked to create one. */
  onCreate?: (value: string) => void;
  /** Edit mode only: a "+" at the end of the row, for a value the list does not have. */
  onAdd?: () => void;
}

export function group(
  label: string,
  options: readonly string[],
  available: readonly string[],
  current: string | null | undefined,
  onPick: (value: string) => void,
  opts: GroupOptions = {},
): HTMLDivElement {
  const box = el("div", { class: "btns" });
  for (const o of options) {
    const empty = !available.includes(o);
    // A spot the rules forbid is never offered, charted or not: there is no gap to fill,
    // because no table ever deals that hand.
    const fault = opts.faultOf ? opts.faultOf(o) : "";
    const onCreate = empty && !fault && opts.editing ? opts.onCreate : undefined;
    const dot = opts.dotOf ? opts.dotOf(o) : "";
    const b = el(
      "button",
      {
        type: "button",
        class: "btn" + (o === current ? " on" : "") + (onCreate ? " ghost" : ""),
        disabled: fault ? true : empty && !onCreate,
        onclick: () => (onCreate ? onCreate(o) : onPick(o)),
        ...(opts.keyOf ? { dataset: { value: opts.keyOf(o) } } : {}),
      },
      dot ? el("i", { class: "swatch", style: { background: dot } }) : null,
      opts.labelOf ? opts.labelOf(o) : o,
    );
    // The reason a button is dead beats the description of what it would do; otherwise the
    // tooltip spells out a label too short to read on its own.
    const tip = opts.titleOf ? opts.titleOf(o) : "";
    if (fault) b.title = fault;
    else if (onCreate)
      b.title = tip
        ? `${tip} — no chart here yet, click to create one`
        : "No chart here yet — create one";
    else if (empty) b.title = tip ? `${tip} — no chart here` : "No chart here";
    else if (tip) b.title = tip;
    box.append(b);
  }
  const { onAdd } = opts;
  if (opts.editing && onAdd)
    box.append(
      el(
        "button",
        {
          type: "button",
          class: "btn add",
          title: `Add a ${label.toLowerCase()} the list does not have`,
          onclick: () => onAdd(),
        },
        "+",
      ),
    );
  return el(
    "div",
    { class: "group", ...(opts.dim ? { dataset: { dim: opts.dim } } : {}) },
    el("span", {}, label),
    box,
  );
}
